/* Build in libraries */
import { useState } from 'react';

/* Third party libraries */

/* Components */
import Card from '@/components/UI/Card/index.tsx';
import StatCard from '@/components/UI/Card/StatCard';
import PieChart from '@/components/UI/Charts/PieChart';
import { calcTotalHouseholdIncome, convertIntoValueAndPercentage } from '@/features/ApplicationForm/FormConfig/helpers';
/* Services */
import { races } from '../ApplicationForm/ApplicationInformation/constants';

/* Assets */

interface IApplicationDashboardComponentProps {
  data: any;
  defaultView?: 'ALL' | 'APPROVED';
  hideSwitch?: boolean;
}

interface IDoughnutChart {
  dataLabels: string[];
  dataSets: { data: number; color: string }[];
}

interface IPieChart {
  label: string[];
  dataSets: { data: number; color: string }[];
}

export const Dashboard = ({ data, defaultView = 'ALL', hideSwitch }: IApplicationDashboardComponentProps) => {
  const [viewStatus, setViewStatus] = useState<string>(defaultView);
  const applicationsList = data?.data;

  const widgetsData: {
    totalApplications: number;
    totalHousePrice: number;
    statusSummary: { [key: string]: number };
    totalCityEmployees: IDoughnutChart;
    gender: IDoughnutChart;
    totalHouseholdIncome: number;
    maritalStatus: IPieChart;
    totalRaces: IPieChart;
  } = {
    totalApplications: 0,
    totalHousePrice: 0,
    statusSummary: {},
    totalCityEmployees: {
      dataLabels: ['Yes', 'No'],
      dataSets: [
        { data: 0, color: '#D9B64E' },
        { data: 0, color: '#0FCA98' },
      ],
    },
    gender: {
      dataLabels: ['Female', 'Male'],
      dataSets: [
        { data: 0, color: '#D9B64E' },
        { data: 0, color: '#0FCA98' },
      ],
    },
    totalHouseholdIncome: 0,
    maritalStatus: {
      label: ['Married', 'Unmarried', 'Widowed'],
      dataSets: [
        { data: 0, color: '#0FCA98' },
        { data: 0, color: '#D9B64E' },
        { data: 0, color: '#2B6CB0' },
      ],
    },
    totalRaces: {
      label: races,
      dataSets: [
        { data: 0, color: '#D9B64E' },
        { data: 0, color: '#0FCA98' },
        { data: 0, color: '#2B6CB0' },
        { data: 0, color: '#F27141' },
      ],
    },
  };

  applicationsList?.forEach((a: any) => {
    const { applicationData } = a;
    //Total Application Widget
    widgetsData.totalApplications += 1;

    // Total Amount Requested
    widgetsData.totalHousePrice += applicationData?.propertyInformation?.housePrice ? applicationData?.propertyInformation?.housePrice : 0;

    //Status Summary
    widgetsData.statusSummary[a.status] = (widgetsData.statusSummary[a.status] ?? 0) + 1;

    if (applicationData?.application?.cityEmployee === 'yes') {
      widgetsData.totalCityEmployees.dataSets[0].data += 1;
    }
    if (applicationData?.application?.cityEmployee === 'no') {
      widgetsData.totalCityEmployees.dataSets[1].data += 1;
    }

    // gender
    if (applicationData?.application?.gender === 'Female') {
      widgetsData.gender.dataSets[0].data += 1;
    }
    if (applicationData?.application?.gender === 'Male') {
      widgetsData.gender.dataSets[1].data += 1;
    }

    // marital status
    if (applicationData?.application?.maritalStatus === 'Un-Married') {
      widgetsData.maritalStatus.dataSets[1].data += 1;
    }
    if (applicationData?.application?.maritalStatus === 'Married') {
      widgetsData.maritalStatus.dataSets[0].data += 1;
    }
    if (applicationData?.application?.maritalStatus === 'Widow') {
      widgetsData.maritalStatus.dataSets[2].data += 1;
    }

    // Total Household Income
    widgetsData.totalHouseholdIncome += calcTotalHouseholdIncome(applicationData);

    // Total Races
    applicationData?.application?.races?.forEach((r: any) => {
      if (r === 'African-American') widgetsData.totalRaces.dataSets[0].data += 1;
      if (r === 'Caucasian') widgetsData.totalRaces.dataSets[1].data += 1;
      if (r === 'Hispanic') widgetsData.totalRaces.dataSets[2].data += 1;
      if (r === 'Other') widgetsData.totalRaces.dataSets[3].data += 1;
    });
  });

  return (
    <div className='max-w-screen-3xl mx-auto'>
      {!hideSwitch && (
        <div className='font-semibold text-bold py-4 rounded-md space-x-2'>
          <span
            className={`${
              viewStatus === 'ALL' ? 'bg-primaryColor-normal' : 'bg-neutralColor-light hover:bg-primaryColor-normal'
            } text-white px-4 py-2 cursor-pointer`}
            onClick={() => setViewStatus('ALL')}
          >
            All Submissions
          </span>
          <span
            className={`${
              viewStatus === 'APPROVED' ? 'bg-primaryColor-normal' : 'bg-neutralColor-light hover:bg-primaryColor-normal'
            } text-white px-4 py-2 cursor-pointer`}
            onClick={() => setViewStatus('APPROVED')}
          >
            Approved Submissions
          </span>
        </div>
      )}

      <div className='my-4'></div>
      {viewStatus && (
        <div className='my-4'>
          <section className='my-4 grid md:grid-cols-2 xl:grid-cols-4 gap-4'>
            <StatCard title='Total Households' stat={974} />
            <StatCard title='Average Household Income' stat={'$49 685'} />
            <StatCard title='Average Household Size' stat={'3.5'} />
          </section>
          <section className='grid md:grid-cols-2 xl:grid-cols-2 gap-4 mt-4'>
            <Card title='Head of Household Gender'>
              <div className='p-5 flex flex-col items-center '>
                <div className='w-5/6 lg:w-2/3 h-80'>
                  <PieChart
                    labels={widgetsData?.gender?.dataLabels}
                    datasets={convertIntoValueAndPercentage(widgetsData?.gender?.dataSets)}
                    showValueAndPercentage
                  />
                </div>
              </div>
            </Card>
            <Card title='Head of Household Race'>
              <div className='p-5 flex flex-col items-center '>
                <div className='w-5/6 lg:w-4/5 h-80'>
                  <PieChart
                    labels={widgetsData?.totalRaces?.label}
                    datasets={convertIntoValueAndPercentage(widgetsData?.totalRaces?.dataSets)}
                    chartTitle=''
                    showValueAndPercentage
                  />
                </div>
              </div>
            </Card>
          </section>
          <section className='grid md:grid-cols-2 xl:grid-cols-2 gap-4 mt-4'>
            <Card title='Marital Status'>
              <div className='p-5 flex flex-col items-center '>
                <div className='w-5/6 lg:w-2/3 h-80'>
                  <PieChart
                    labels={widgetsData?.maritalStatus?.label}
                    datasets={convertIntoValueAndPercentage(widgetsData?.maritalStatus?.dataSets)}
                    showValueAndPercentage
                    chartTitle=''
                  />
                </div>
              </div>
            </Card>
          </section>
        </div>
      )}
    </div>
  );
};
